import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface Credential {
  id: number;
  type: string;
  number: string;
  issuer: string;
  issued: string;
  expires: string;
  status: string;
  description: string;
  icon: string;
}

export default function CredentialsHero() {
  const [selectedCredential, setSelectedCredential] = useState<any>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [editingField, setEditingField] = useState<{ id: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Load credentials from localStorage or use defaults
  useEffect(() => {
    const saved = localStorage.getItem('credentials');
    if (saved) {
      setCredentials(JSON.parse(saved));
    } else {
      const defaults: Credential[] = [
        {
          id: 1,
          type: 'DOT Number',
          number: 'Click to edit',
          issuer: 'Click to edit',
          issued: '2024-01-15',
          expires: '2026-01-15',
          status: 'Active',
          description: 'Click to edit description',
          icon: 'ri-truck-line',
        },
        {
          id: 2,
          type: 'MC Number',
          number: 'Click to edit',
          issuer: 'Click to edit',
          issued: '2024-01-15',
          expires: '2026-01-15',
          status: 'Active',
          description: 'Click to edit description',
          icon: 'ri-shield-star-line',
        },
        {
          id: 3,
          type: 'State License',
          number: 'Click to edit',
          issuer: 'Click to edit',
          issued: '2024-01-10',
          expires: '2025-01-10',
          status: 'Active',
          description: 'Click to edit description',
          icon: 'ri-file-shield-2-line',
        },
        {
          id: 4,
          type: 'Insurance Certificate',
          number: 'Click to edit',
          issuer: 'Click to edit',
          issued: '2024-01-01',
          expires: '2025-01-01',
          status: 'Active',
          description: 'Click to edit description',
          icon: 'ri-shield-check-line',
        },
      ];
      setCredentials(defaults);
      localStorage.setItem('credentials', JSON.stringify(defaults));
    }
  }, []);

  const saveCredentials = (updated: Credential[]) => {
    setCredentials(updated);
    localStorage.setItem('credentials', JSON.stringify(updated));
  };

  const startEdit = (id: number, field: string, currentValue: string) => {
    setEditingField({ id, field });
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    if (!editingField) return;
    
    const updated = credentials.map(cred => {
      if (cred.id === editingField.id) {
        return { ...cred, [editingField.field]: editValue };
      }
      return cred;
    });
    
    saveCredentials(updated);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const resetToDefaults = () => {
    if (confirm('Reset all credentials to default values?')) {
      localStorage.removeItem('credentials');
      window.location.reload();
    }
  };

  const renderEditableField = (id: number, field: string, value: string, className: string = '') => {
    const isEditing = editingField?.id === id && editingField?.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            className="bg-white/20 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1"
            autoFocus
          />
          <button onClick={saveEdit} className="text-green-400 hover:text-green-300 cursor-pointer">
            <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          </button>
          <button onClick={cancelEdit} className="text-red-400 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      );
    }
    
    return (
      <div
        onClick={() => startEdit(id, field, value)}
        className={`${className} cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-all duration-200 group relative`}
      >
        {value}
        <i className="ri-pencil-line absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-electric-blue text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
    );
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-900 to-dark-800 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-electric-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-electric-cyan/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            OFFICIAL <span className="text-electric-blue">DOCUMENTATION</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-4">
            All credentials verified and up to date
          </p>
          <button
            onClick={resetToDefaults}
            className="text-white/40 hover:text-electric-blue text-sm font-inter transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-refresh-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
            Reset to Defaults
          </button>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {credentials.map((credential, index) => (
            <motion.div
              key={credential.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => setSelectedCredential(credential)}
              className="group cursor-pointer"
            >
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:bg-white/15 hover:border-electric-blue/50 transition-all duration-300 hover:shadow-2xl hover:shadow-electric-blue/20 h-full">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-electric-blue to-electric-cyan flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <i className={`${credential.icon} text-3xl text-dark-900 w-8 h-8 flex items-center justify-center`}></i>
                  </div>
                  <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-500/20 border border-green-500/50 rounded-full">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="font-inter text-green-400 text-xs font-semibold">
                      {credential.status}
                    </span>
                  </div>
                </div>

                <h3 className="font-orbitron text-xl font-bold text-white mb-2">
                  {credential.type}
                </h3>

                <div className="font-orbitron text-3xl font-bold text-electric-blue mb-4">
                  {credential.number}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-white/60 text-sm font-inter">
                    <i className="ri-building-line w-4 h-4 flex items-center justify-center"></i>
                    <span>{credential.issuer}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-white/60 text-sm font-inter">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    <span>Expires: {new Date(credential.expires).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </div>

                <button className="flex items-center space-x-2 text-electric-blue font-inter text-sm font-semibold group-hover:space-x-3 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  <span>View Details</span>
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Credential Detail Modal */}
        {selectedCredential && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => setSelectedCredential(null)}
            className="fixed inset-0 bg-dark-900/95 backdrop-blur-xl z-50 flex items-center justify-center p-6 cursor-pointer"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 max-w-2xl w-full cursor-default"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-electric-blue to-electric-cyan flex items-center justify-center">
                    <i className={`${selectedCredential.icon} text-3xl text-dark-900 w-8 h-8 flex items-center justify-center`}></i>
                  </div>
                  <div>
                    <h3 className="font-orbitron text-2xl font-bold text-white mb-1">
                      {selectedCredential.type}
                    </h3>
                    <div className="font-orbitron text-electric-blue text-lg">
                      {renderEditableField(selectedCredential.id, 'number', selectedCredential.number, 'font-orbitron text-electric-blue text-lg')}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCredential(null)}
                  className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-electric-blue/20 hover:border-electric-blue transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-white w-5 h-5 flex items-center justify-center"></i>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-1">
                    ISSUING AUTHORITY
                  </div>
                  <div className="font-inter text-white text-base">
                    {renderEditableField(selectedCredential.id, 'issuer', selectedCredential.issuer, 'font-inter text-white text-base')}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-1">
                      ISSUED DATE
                    </div>
                    <div className="font-inter text-white text-base">
                      {renderEditableField(selectedCredential.id, 'issued', selectedCredential.issued, 'font-inter text-white text-base')}
                    </div>
                  </div>
                  <div>
                    <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-1">
                      EXPIRATION DATE
                    </div>
                    <div className="font-inter text-white text-base">
                      {renderEditableField(selectedCredential.id, 'expires', selectedCredential.expires, 'font-inter text-white text-base')}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-orbitron text-xs text-electric-blue tracking-wider mb-1">
                    DESCRIPTION
                  </div>
                  <div className="font-inter text-white/80 text-sm leading-relaxed">
                    {renderEditableField(selectedCredential.id, 'description', selectedCredential.description, 'font-inter text-white/80 text-sm leading-relaxed')}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center space-x-2 pt-6 border-t border-white/10">
                <i className="ri-checkbox-circle-fill text-green-400 text-2xl w-6 h-6 flex items-center justify-center"></i>
                <span className="font-inter text-green-400 font-semibold">
                  Verified & Active
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
